/** 本文中のメンショントークン @[名前](職員ID) */
const MENTION_TOKEN = /@\[([^\]]+)\]\((\d+)\)/g;

const OPEN_RED = '{{red}}';
const CLOSE_RED = '{{/red}}';
const OPEN_BLUE = '{{blue}}';
const CLOSE_BLUE = '{{/blue}}';
const OPEN_BLACK = '{{black}}';
const CLOSE_BLACK = '{{/black}}';

export type BulletinBodySegment =
  | { type: 'text'; text: string }
  | { type: 'mention'; displayName: string; staffId: number };

/** メンション以外の本文断片を太字・色で再帰分割したノード */
export type FormattedRun =
  | { kind: 'plain'; text: string }
  | { kind: 'bold'; children: FormattedRun[] }
  | { kind: 'red'; children: FormattedRun[] }
  | { kind: 'blue'; children: FormattedRun[] }
  | { kind: 'black'; children: FormattedRun[] };

/**
 * 掲示板本文をプレーンテキストとメンションに分割する
 */
export function parseBulletinBodySegments(body: string): BulletinBodySegment[] {
  const segments: BulletinBodySegment[] = [];
  let lastIndex = 0;
  const re = new RegExp(MENTION_TOKEN.source, 'g');
  let m: RegExpExecArray | null;
  while ((m = re.exec(body)) !== null) {
    if (m.index > lastIndex) {
      segments.push({ type: 'text', text: body.slice(lastIndex, m.index) });
    }
    const staffId = Number.parseInt(m[2], 10);
    if (Number.isInteger(staffId) && staffId >= 1) {
      segments.push({ type: 'mention', displayName: m[1], staffId });
    } else {
      segments.push({ type: 'text', text: m[0] });
    }
    lastIndex = m.index + m[0].length;
  }
  if (lastIndex < body.length) {
    segments.push({ type: 'text', text: body.slice(lastIndex) });
  }
  return segments;
}

/**
 * テキスト断片を **太字** と {{red}} / {{blue}} / {{black}} で再帰パースする（メンションは含まない想定）
 */
export function parseFormattedRuns(text: string): FormattedRun[] {
  const result: FormattedRun[] = [];
  let i = 0;
  const kindOrder = { bold: 0, red: 1, blue: 2, black: 3 };
  while (i < text.length) {
    const boldAt = text.indexOf('**', i);
    const redAt = text.indexOf(OPEN_RED, i);
    const blueAt = text.indexOf(OPEN_BLUE, i);
    const blackAt = text.indexOf(OPEN_BLACK, i);

    type Kind = 'bold' | 'red' | 'blue' | 'black';
    const cands: { kind: Kind; pos: number }[] = [];
    if (boldAt >= 0) cands.push({ kind: 'bold', pos: boldAt });
    if (redAt >= 0) cands.push({ kind: 'red', pos: redAt });
    if (blueAt >= 0) cands.push({ kind: 'blue', pos: blueAt });
    if (blackAt >= 0) cands.push({ kind: 'black', pos: blackAt });
    if (cands.length === 0) {
      if (i < text.length) {
        result.push({ kind: 'plain', text: text.slice(i) });
      }
      break;
    }
    cands.sort((a, b) => a.pos - b.pos || kindOrder[a.kind] - kindOrder[b.kind]);

    const first = cands[0];
    if (first.pos > i) {
      result.push({ kind: 'plain', text: text.slice(i, first.pos) });
    }
    i = first.pos;

    if (first.kind === 'bold') {
      const end = text.indexOf('**', i + 2);
      if (end === -1) {
        result.push({ kind: 'plain', text: text.slice(i) });
        break;
      }
      const inner = text.slice(i + 2, end);
      result.push({ kind: 'bold', children: parseFormattedRuns(inner) });
      i = end + 2;
    } else if (first.kind === 'red') {
      const close = text.indexOf(CLOSE_RED, i + OPEN_RED.length);
      if (close === -1) {
        result.push({ kind: 'plain', text: text.slice(i) });
        break;
      }
      const inner = text.slice(i + OPEN_RED.length, close);
      result.push({ kind: 'red', children: parseFormattedRuns(inner) });
      i = close + CLOSE_RED.length;
    } else if (first.kind === 'blue') {
      const close = text.indexOf(CLOSE_BLUE, i + OPEN_BLUE.length);
      if (close === -1) {
        result.push({ kind: 'plain', text: text.slice(i) });
        break;
      }
      const inner = text.slice(i + OPEN_BLUE.length, close);
      result.push({ kind: 'blue', children: parseFormattedRuns(inner) });
      i = close + CLOSE_BLUE.length;
    } else {
      const close = text.indexOf(CLOSE_BLACK, i + OPEN_BLACK.length);
      if (close === -1) {
        result.push({ kind: 'plain', text: text.slice(i) });
        break;
      }
      const inner = text.slice(i + OPEN_BLACK.length, close);
      result.push({ kind: 'black', children: parseFormattedRuns(inner) });
      i = close + CLOSE_BLACK.length;
    }
  }
  return result;
}

function flattenFormattedRunsToPlain(runs: FormattedRun[]): string {
  return runs
    .map((r) => {
      if (r.kind === 'plain') return r.text;
      return flattenFormattedRunsToPlain(r.children);
    })
    .join('');
}

/** 書式マーカーを除いた平文（プレビュー用） */
export function stripBulletinFormatMarkers(text: string): string {
  return flattenFormattedRunsToPlain(parseFormattedRuns(text));
}
